import { Box, Typography, CircularProgress, Alert, Pagination } from '@mui/material';
import { StarRating } from './StarRating';
import { ReviewCard } from './ReviewCard';
import { useVenueReviews } from '../hooks/useReviews';
import { useState } from 'react';

interface ReviewsSectionProps {
  venueId: string;
  averageRating?: number;
  totalReviews?: number;
}

/**
 * ReviewsSection - Display venue reviews with pagination
 *
 * Shows aggregate rating stats and a paginated list of reviews.
 * Handles loading and error states.
 *
 * @example
 * <ReviewsSection
 *   venueId={venue.id}
 *   averageRating={venue.averageRating}
 *   totalReviews={venue.totalReviews}
 * />
 */
export function ReviewsSection({
  venueId,
  averageRating = 0,
  totalReviews = 0,
}: ReviewsSectionProps) {
  const [page, setPage] = useState(1);
  const limit = 10;

  const { data, isLoading, error } = useVenueReviews(venueId, { page, limit });

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    // Scroll to top of reviews section
    document.getElementById('reviews-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box id="reviews-section" sx={{ mt: 4 }}>
      {/* Header with aggregate stats */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            mb: 2,
            fontFamily: '"IBM Plex Sans", sans-serif',
          }}
        >
          Customer Reviews
        </Typography>

        {totalReviews > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <StarRating rating={averageRating} size={24} showValue />
            <Typography variant="body2" color="text.secondary">
              Based on {totalReviews} {totalReviews === 1 ? 'review' : 'reviews'}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Loading state */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load reviews. Please try again later.
        </Alert>
      )}

      {/* Reviews list */}
      {!isLoading && !error && data && (
        <>
          {data.data.length === 0 ? (
            <Box
              sx={{
                textAlign: 'center',
                py: 6,
                px: 2,
                backgroundColor: 'background.paper',
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No reviews yet. Be the first to review this venue!
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                {data.data.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </Box>

              {/* Pagination */}
              {data.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination
                    count={data.totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    size="large"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
}
